import { useMemo } from 'react';

import { KruskalResultsV2, KruskalResultsDataV2 } from 'api/data/analyses';
import { Table } from 'components/UI/Table';
import { useTranslation } from 'hooks/store';
import { parseAnalysisNumber } from 'store/data/analyses/parsers';
import { AnalysisStatistic } from 'components/Analysis/Analyses';
import { useAnalysisErrorMessage } from 'hooks/store/data/analysis/useAnalysisErrorMessage';

interface Props {
	results: KruskalResultsV2;
}

export function CompareNumericKruskal({ results }: Props) {
	const { translate } = useTranslation();

	const { data, error, warnings } = results;

	const translatedMessage = useAnalysisErrorMessage(error);

	return (
		<AnalysisStatistic>
			<AnalysisStatistic.Title>
				{translate(({ analysis }) => analysis.statistics.kruskal.name)}
			</AnalysisStatistic.Title>

			{error && (
				<AnalysisStatistic.Error>
					{translatedMessage ??
						translate(({ errors }) => errors.api.analyses.statistics.kruskal)}
				</AnalysisStatistic.Error>
			)}

			{data && <Result data={data} />}

			{!!warnings && (
				<AnalysisStatistic.Hint>
					{warnings.message ??
						translate(({ analysis }) => analysis.errors.statistics.invalidKruskal)}
				</AnalysisStatistic.Hint>
			)}
		</AnalysisStatistic>
	);
}

function Result({ data }: { data: KruskalResultsDataV2 }) {
	const { dfStatistic, pStatistic } = useMemo(() => {
		const dfStatistic = getDfStatistic({
			df: data.df,
			stat: data.statistic
		});

		const pStatistic = getPStatistic({
			pValue: data.pValue
		});

		return { dfStatistic, pStatistic };
	}, [data]);

	return (
		<Table.Responsive horizontalScroll fullWidth={false}>
			<Table>
				<Table.Body>
					<Table.Row>
						<Table.Cell title={dfStatistic.text} width={14} noWrap>
							{dfStatistic.JSX}
						</Table.Cell>
						<Table.Cell title={pStatistic.text} width={14} noWrap>
							{pStatistic.JSX}
						</Table.Cell>
					</Table.Row>
				</Table.Body>
			</Table>
		</Table.Responsive>
	);
}

function getDfStatistic(input: { df: number; stat: number }) {
	const { df, stat } = input;

	const { operator, value: parsedStat } = parseAnalysisNumber(stat);

	const output = {
		JSX: (
			<>
				<i>{`H`}</i>
				{`(${df}) ${operator} ${parsedStat}`}
			</>
		),
		text: `H(${df}) ${operator} ${parsedStat}`
	};

	return output;
}

function getPStatistic(input: { pValue: number }) {
	const { pValue } = input;

	const { operator, value: parsedPValue } = parseAnalysisNumber(pValue, {
		decimals: 3,
		formatAsPValue: true
	});

	const output = {
		JSX: (
			<>
				<i>{`p`}</i>
				{` ${operator} ${parsedPValue}`}
			</>
		),
		text: `p ${operator} ${parsedPValue}`
	};

	return output;
}
